// ForcedPasswordOverlay.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { verifyPassword, updatePassword } from '../utils/api';

const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const ShieldCheck = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
    </svg>
);

const EyeToggle = ({ show, onClick }) => (
    <button type="button" onClick={onClick} style={s.eyeBtn}>
        {show ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
        ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        )}
    </button>
);

const getStrength = (pw) => {
    if (!pw) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (pw.length >= 6) score++;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: 'Weak', color: '#f87171', width: '20%' };
    if (score === 2) return { label: 'Fair', color: '#fbbf24', width: '40%' };
    if (score === 3) return { label: 'Good', color: '#22d3ee', width: '60%' };
    if (score === 4) return { label: 'Strong', color: '#4ade80', width: '80%' };
    return { label: 'Very Strong', color: '#4ade80', width: '100%' };
};

const ForcedPasswordOverlay = () => {
    const [formData, setFormData] = useState({
        tempPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showTemp, setShowTemp] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [errors, setErrors] = useState({});
    const [tempVerified, setTempVerified] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifiedPassword, setVerifiedPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState(null);
    const verifyTimerRef = useRef(null);

    // Auto-dismiss toast
    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    // Block ESC, back button, page reload while forced
    useEffect(() => {
        const blockEsc = (e) => {
            if (e.key === 'Escape') e.preventDefault();
        };
        const blockBack = (e) => {
            e.preventDefault();
            e.returnValue = 'You must set a new password before leaving.';
            return e.returnValue;
        };
        
        window.addEventListener('keydown', blockEsc);
        window.addEventListener('beforeunload', blockBack);
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        
        return () => {
            window.removeEventListener('keydown', blockEsc);
            window.removeEventListener('beforeunload', blockBack);
            document.body.style.overflow = '';
        };
    }, []);

    const doVerify = useCallback(async (password) => {
        if (!password || password.length < 1) {
            setTempVerified(null);
            setVerifiedPassword('');
            return;
        }
        setIsVerifying(true);
        try {
            const result = await verifyPassword(password);
            if (result.verified) {
                setTempVerified(true);
                setVerifiedPassword(password);
            } else {
                setTempVerified(false);
                setVerifiedPassword('');
            }
        } catch {
            setTempVerified(false);
            setVerifiedPassword('');
        } finally {
            setIsVerifying(false);
        }
    }, []);

    const handleTempPasswordChange = (value) => {
        setFormData((prev) => ({ ...prev, tempPassword: value }));

        if (value !== verifiedPassword) {
            setTempVerified(null);
            setVerifiedPassword('');
        }

        if (tempVerified === true && value !== verifiedPassword) {
            setFormData((prev) => ({ ...prev, tempPassword: value, newPassword: '', confirmPassword: '' }));
            setErrors({});
        }

        if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
        verifyTimerRef.current = setTimeout(() => doVerify(value), 1200);
    };

    const handleVerifyClick = () => {
        if (formData.tempPassword) doVerify(formData.tempPassword);
    };

    const setValue = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        const e = { ...errors };

        if (field === 'newPassword') {
            if (value && value.length < 6) e.newPassword = 'Password must be at least 6 characters';
            else if (value && /\s/.test(value)) e.newPassword = 'Password cannot contain spaces';
            else if (value && value === formData.tempPassword) e.newPassword = 'New password must differ from temporary';
            else delete e.newPassword;
            if (formData.confirmPassword && formData.confirmPassword !== value) e.confirmPassword = 'Passwords do not match';
            else delete e.confirmPassword;
        }

        if (field === 'confirmPassword') {
            if (value && value !== formData.newPassword) e.confirmPassword = 'Passwords do not match';
            else delete e.confirmPassword;
        }

        setErrors(e);
    };

    const isFormValid = () =>
        tempVerified === true &&
        formData.tempPassword === verifiedPassword &&
        formData.newPassword.length >= 6 &&
        !/\s/.test(formData.newPassword) &&
        formData.newPassword !== formData.tempPassword &&
        formData.newPassword === formData.confirmPassword &&
        Object.keys(errors).length === 0;

    const handleSubmit = async () => {
        if (!isFormValid() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const result = await updatePassword(formData.tempPassword, formData.newPassword);
            if (result.success) {
                setToast({ type: 'success', message: 'Password set. Redirecting to login...' });
                setTimeout(() => { window.location.href = '/'; }, 2000);
            } else {
                setToast({ type: 'error', message: result.message || 'Failed' });
                setIsSubmitting(false);
            }
        } catch (err) {
            setToast({ type: 'error', message: err.message || 'Failed' });
            setIsSubmitting(false);
        }
    };

    const strength = getStrength(formData.newPassword);
    const checks = [
        { label: 'Temp password verified', pass: tempVerified === true },
        { label: 'Min 6 characters', pass: formData.newPassword.length >= 6 },
        { label: 'No spaces', pass: formData.newPassword.length > 0 && !/\s/.test(formData.newPassword) },
        { label: 'Different from temp', pass: formData.newPassword.length > 0 && formData.newPassword !== formData.tempPassword },
        { label: 'Passwords match', pass: formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword },
    ];

    return (
        <div style={s.overlay}>
            <style>{s.keyframes}</style>

            {toast && (
                <div style={{
                    ...s.toast,
                    ...(toast.type === 'success' ? s.toastSuccess : s.toastError),
                }}>
                    {toast.message}
                </div>
            )}

            <div style={s.modal}>
                <div style={s.warningBanner}>
                    <LockIcon />
                    <span>You're using a temporary password. Set a new password to continue.</span>
                </div>

                <h2 style={s.title}>Set Your Password</h2>
                <p style={s.subtitle}>This step is required and cannot be skipped</p>

                {/* Step 1: Verify temp password */}
                <div style={s.section}>
                    <div style={s.sectionLabel}>Step 1 — Verify Temporary Password</div>
                    <div style={s.fieldGroup}>
                        <label style={s.label}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <LockIcon /> Temporary Password <span style={{ color: '#f87171' }}>*</span>
                            </span>
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showTemp ? 'text' : 'password'}
                                value={formData.tempPassword}
                                onChange={(e) => handleTempPasswordChange(e.target.value)}
                                placeholder="Enter the 6-digit code"
                                style={{
                                    ...s.input,
                                    paddingRight: 42,
                                    ...(tempVerified === true ? s.inputVerified : {}),
                                    ...(tempVerified === false ? s.inputError : {}),
                                }}
                                autoFocus
                            />
                            <EyeToggle show={showTemp} onClick={() => setShowTemp(!showTemp)} />
                        </div>

                        {formData.tempPassword && tempVerified !== true && (
                            <button
                                onClick={handleVerifyClick}
                                disabled={isVerifying}
                                style={{
                                    ...s.verifyBtn,
                                    ...(isVerifying ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                                }}
                            >
                                {isVerifying ? 'Verifying...' : 'Verify'}
                            </button>
                        )}

                        {tempVerified !== null && (
                            <div style={{
                                ...s.verifyCard,
                                ...(tempVerified ? s.verifyCardSuccess : s.verifyCardError),
                            }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: tempVerified ? '#4ade80' : '#f87171' }}>
                                    {tempVerified ? '✓ Verified' : '✗ Incorrect'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 2: New password — only shows after verification */}
                {tempVerified === true && (
                    <>
                        <div style={s.section}>
                            <div style={s.sectionLabel}>Step 2 — Choose New Password</div>
                            
                            <div style={s.fieldGroup}>
                                <label style={s.label}>New Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showNew ? 'text' : 'password'}
                                        value={formData.newPassword}
                                        onChange={(e) => setValue('newPassword', e.target.value)}
                                        placeholder="Min 6 characters, no spaces"
                                        style={{
                                            ...s.input,
                                            paddingRight: 42,
                                            ...(errors.newPassword ? s.inputError : {}),
                                        }}
                                    />
                                    <EyeToggle show={showNew} onClick={() => setShowNew(!showNew)} />
                                </div>
                                {errors.newPassword && <div style={s.errorMsg}>{errors.newPassword}</div>}
                                
                                {formData.newPassword && !errors.newPassword && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={s.strengthTrack}>
                                            <div style={{ ...s.strengthFill, width: strength.width, background: strength.color }} />
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: strength.color, marginTop: 4 }}>{strength.label}</div>
                                    </div>
                                )}
                            </div>

                            <div style={{ ...s.fieldGroup, marginTop: 14 }}>
                                <label style={s.label}>Confirm New Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setValue('confirmPassword', e.target.value)}
                                        placeholder="Re-enter new password"
                                        style={{
                                            ...s.input,
                                            paddingRight: 42,
                                            ...(errors.confirmPassword ? s.inputError : {}),
                                        }}
                                    />
                                    <EyeToggle show={showConfirm} onClick={() => setShowConfirm(!showConfirm)} />
                                </div>
                                {errors.confirmPassword && <div style={s.errorMsg}>{errors.confirmPassword}</div>}
                            </div>
                        </div>

                        {formData.newPassword && (
                            <div style={s.checklistCard}>
                                {checks.map((c, i) => (
                                    <div key={i} style={s.checkRow}>
                                        <span style={{ color: c.pass ? '#4ade80' : 'rgba(255,255,255,0.2)' }}>
                                            {c.pass ? '✓' : '○'}
                                        </span>
                                        <span style={{ fontSize: '0.8rem', color: c.pass ? '#4ade80' : 'rgba(255,255,255,0.35)' }}>
                                            {c.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={!isFormValid() || isSubmitting}
                            style={{
                                ...s.submitBtn,
                                ...((!isFormValid() || isSubmitting) ? s.submitBtnDisabled : {}),
                            }}
                        >
                            {isSubmitting ? 'Updating...' : 'Set Password & Continue'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

const s = {
    keyframes: `@keyframes spin { to { transform: rotate(360deg); } }`,
    
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
    },
    modal: {
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        border: '1px solid rgba(34, 211, 238, 0.3)',
        borderRadius: 20,
        padding: 32,
        maxWidth: 480,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
        color: '#f1f5f9',
        fontFamily: "'DM Sans', sans-serif",
    },
    warningBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 16px',
        borderRadius: 10,
        background: 'rgba(245,158,11,0.15)',
        border: '1px solid rgba(245,158,11,0.3)',
        color: '#fbbf24',
        fontSize: '0.82rem',
        fontWeight: 500,
        marginBottom: 20,
    },
    title: {
        fontSize: '1.4rem',
        fontWeight: 700,
        margin: '0 0 4px 0',
    },
    subtitle: {
        fontSize: '0.85rem',
        color: 'rgba(255,255,255,0.5)',
        marginBottom: 24,
        marginTop: 0,
    },
    section: { marginBottom: 24 },
    sectionLabel: {
        fontSize: '0.72rem',
        fontWeight: 600,
        color: '#22d3ee',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 12,
    },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: '0.78rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)' },
    input: {
        padding: '10px 14px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.07)',
        color: '#f1f5f9',
        fontSize: '0.9rem',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    inputError: { borderColor: 'rgba(239,68,68,0.5)', background: 'rgba(239,68,68,0.05)' },
    inputVerified: { borderColor: 'rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.05)' },
    eyeBtn: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        color: 'rgba(255,255,255,0.4)',
    },
    errorMsg: { fontSize: '0.75rem', color: '#f87171', marginTop: 4 },
    verifyBtn: {
        marginTop: 8,
        padding: '10px 16px',
        borderRadius: 10,
        border: '1px solid rgba(34,211,238,0.3)',
        background: 'rgba(34,211,238,0.1)',
        color: '#22d3ee',
        fontSize: '0.85rem',
        fontWeight: 600,
        cursor: 'pointer',
        width: '100%',
    },
    verifyCard: { marginTop: 12, padding: '10px 14px', borderRadius: 10 },
    verifyCardSuccess: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)' },
    verifyCardError: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' },
    strengthTrack: { height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
    strengthFill: { height: '100%', borderRadius: 2, transition: 'all 0.3s ease' },
    checklistCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 14,
        borderRadius: 10,
        background: 'rgba(255,255,255,0.03)',
        marginBottom: 20,
    },
    checkRow: { display: 'flex', alignItems: 'center', gap: 8 },
    submitBtn: {
        width: '100%',
        padding: '14px 20px',
        borderRadius: 12,
        border: 'none',
        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        color: '#fff',
        fontSize: '0.95rem',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(34,197,94,0.25)',
    },
    submitBtnDisabled: {
        background: 'rgba(255,255,255,0.06)',
        color: 'rgba(255,255,255,0.25)',
        boxShadow: 'none',
        cursor: 'not-allowed',
    },
    toast: {
        position: 'fixed',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '12px 20px',
        borderRadius: 12,
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#fff',
        zIndex: 99999,
        boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    },
    toastSuccess: { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
    toastError: { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
};

export default ForcedPasswordOverlay;